import { getBaseUrl, hasWebpSupport, isAuthenticated } from '@doist/marketist'

import { GetServerSidePropsContext } from 'next/types'
import { cache } from 'services'
import { SupportedCurrencies } from 'services/pricing/price-table'
import { Cookies, Transifex, User } from 'types'

import { getSessionCurrency } from './pricing/session'
import { captureException } from './error-tracking'
import { getUserFromServer } from './getUser'
import { FeatureFlagSession } from './with-session'

type Options = {
    fetchUser?: boolean
}

export type CommonProps = {
    webpSupport: boolean
    cookies: Cookies
    isAuthenticated: boolean
    user?: User
    locale: Transifex.Languages
    currency: SupportedCurrencies
    absoluteUrl: string
    featureFlags: FeatureFlagSession | null
}

export async function getCommonServerSideProps(
    context: GetServerSidePropsContext,
    { fetchUser = false }: Options = {},
): Promise<CommonProps> {
    try {
        const acceptsHeader = { accept: context.req.headers.accept || '' }

        // Detect webp support through 'Accept' request http header
        const webpSupport = hasWebpSupport(acceptsHeader)
        const cookies = context.req.cookies
        const locale = getLocale(context)
        const currency = getSessionCurrency(context)

        // Includes protocol, host and pathname, without query string
        const baseUrl = getBaseUrl(context.req)
        const absoluteUrl = new URL(context.req.url || '', baseUrl)
        absoluteUrl.search = ''

        /** A Cookie is set by the backend when a user is logged in on Twist. */
        const isLoggedIn = Boolean(
            isAuthenticated(cookies as Record<string, string>, process.env.AUTH_COOKIE_KEY),
        )

        const commonProps: CommonProps = {
            webpSupport,
            isAuthenticated: isLoggedIn,
            cookies,
            locale,
            currency,
            absoluteUrl: absoluteUrl.toString(),
            featureFlags: context?.req?.session?.featureFlag ?? null,
        }

        /** Fetch user if needed. */
        if (isLoggedIn && fetchUser) {
            const user = await getUserFromServer(cookies[process.env.AUTH_COOKIE_KEY])
            if (user) {
                commonProps.user = user
            } else {
                commonProps.isAuthenticated = false
            }
        }

        // Reset server in-memory cache if query contains "refresh=true"
        if (context.query.refresh === 'true') {
            cache.reset()
        }

        return commonProps
    } catch (error) {
        if (process.env.APP_ENV !== 'development') {
            captureException(error)
        }

        throw error
    }
}

export function getLocale(context: GetServerSidePropsContext): Transifex.Languages {
    const { NEXT_LOCALE } = context.req.cookies
    return (NEXT_LOCALE as Transifex.Languages) ?? context.locale ?? 'en'
}
